import { SwiperOptions } from 'swiper/types/swiper-options';
import { updateActiveThumb } from './carousel-thumbs';

// https://swiperjs.com/swiper-api

// let the interface extend Swiper options to allow some custom attributes

type ISwiperOptions = {
  [K in keyof SwiperOptions]: K extends 'thumbs'
  ? SwiperOptions[K] &
  Partial<{
    partnerConfig: string;
  }>
  : SwiperOptions[K];
};

interface ISwiperConfigs {
  [key: string]: ISwiperOptions;
}

const defaultConfig: SwiperOptions = {
  breakpoints: {
    768: {
      slidesPerView: 1
    }
  }
};

export function getCarouselConfig(context: HTMLElement, configName: string) {
    const baseConfig = configurations[configName] ?? {};

    return {
        ...baseConfig,
        navigation: {
            ...((baseConfig.navigation as object) ?? {}),
            prevEl: context.querySelector<HTMLElement>('.carousel-button-prev'),
            nextEl: context.querySelector<HTMLElement>('.carousel-button-next'),
            disabledClass: 'swiper-button-disabled is-disabled'
        },
        pagination: {
            ...((baseConfig.pagination as object) ?? {}),
            el: context.querySelector<HTMLElement>('.swiper-pagination')
        },
        scrollbar: {
            ...((baseConfig.scrollbar as object) ?? {}),
            el: context.querySelector<HTMLElement>('.swiper-scrollbar')
        }
    };
}

const configurations = {
  standard: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 16,
    centerInsufficientSlides: true,
    slidesPerView: 4,
    slidesPerGroup: 1,
    breakpoints: {
      0: {
        slidesPerView: 2,
        spaceBetween: 10,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 4,
        spaceBetween: 30,
        slidesPerGroup: 1
      }
    }
  },
  productPageCarousel: {
    slidesPerView: 1,
    spaceBetween: 30,
    pagination: {
      type: 'bullets',
      clickable: true
    },
    centeredSlides: true,
    zoom: {
      toggle: true
    },
    watchSlidesProgress: true,
    on: {
      slideChange(swiper) {
        updateActiveThumb(swiper);
      },
    }
  },
  productPageCarouselThumbs: {
    direction: 'vertical',
    slidesPerView: 'auto',
    slidesPerGroup: 8,
    slidesPerGroupAuto: true,
    spaceBetween: 15,
    autoHeight: false,
    watchOverflow: true,
    freeMode: false,
    watchSlidesProgress: true,
    centeredSlides: true,
    centeredSlidesBounds: true,
    thumbs: {
      partnerConfig: 'productPageCarousel'
    },
  },
  productPageCarouselSingle: {
    slidesPerView: 1,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 30,
    centerInsufficientSlides: true,
    loop: false,
    breakpoints: {
      1200: {
        slidesPerView: 1
      }
    }
  },
  brandPageReviews: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    scrollbar: {
      draggable: true
    }
  },
  reviewPanel: {
    ...defaultConfig
  },

  brandPageProducts: {
    ...defaultConfig,
    scrollbar: {
      draggable: true
    },
    spaceBetween: 25
  },

  simpleAssociatedProducts: {
    ...defaultConfig,
    slidesPerView: 3,
    slidesPerGroup: 3,
    breakpoints: {
      0: {
        slidesPerView: 2,
        spaceBetween: 10,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 30,
        slidesPerGroup: 3
      }
    }
  },
  productPageSimpleAssociatedProducts: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 16,
    centerInsufficientSlides: true,
    slidesPerView: 2,
    slidesPerGroup: 1,
    breakpoints: {
      0: {
        slidesPerView: 2
      },
      768: {
        slidesPerView: 4,
        spaceBetween: 5,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 6,
        spaceBetween: 5,
        slidesPerGroup: 1
      }
    }
  },
  carouselVybeProducts: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 10,
    centerInsufficientSlides: true,
    slidesPerView: 5,
    slidesPerGroup: 1,
    loop: true,
    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 5,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 5,
        spaceBetween: 5,
        slidesPerGroup: 1
      }
    }
  },

  ajax: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    slidesPerView: 1,
    loop: true
  },
  quickview: {
    ...defaultConfig,
    loop: true,
    sliderPerView: 1,
    breakpoints: {
      992: {
        slidesPerView: 2,
        spaceBetween: 5
      },
      768: {
        slidesPerView: 1
      }
    }
  },

  uspslider: {
    loop: true,
    autoplay: {
      pauseOnMouseEnter: true,
      disableOnInteraction: false
    },
    sliderPerView: 1,
    navigation: false
  },

  completeTheOutfit: {
    spaceBetween: 15,
    slidesPerView: 2
  },

  rolexKeepExploring: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 3,
    slidesPerGroup: 3,
    breakpoints: {
      768: {
        slidesPerView: 2,
        spaceBetween: 5,
        slidesPerGroup: 2
      }
    }
  },

  testimonialCarousel: {
    spaceBetween: 32,
    pagination: {
      type: 'bullets'
    },
    slidesPerView: 3,
    breakpoints: {
      1200: {
        slidesPerView: 2
      },
      768: {
        slidesPerView: 1
      }
    }
  },
  pdpImageThumbnails: {
    spaceBetween: 15,
    direction: 'vertical',
    autoHeight: true,
    slidesPerView: 7
  },
  loopPdp: {
    slidesPerView: 1,
    spaceBetween: 30,
    loop: true,
    pagination: {
      type: 'bullets'
    }
  },

  fiveProducts: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 5,
    slidesPerGroup: 5,
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      1200: {
        slidesPerView: 5,
        slidesPerGroup: 5
      }
    }
  },

  threeProducts: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 3,
    slidesPerGroup: 3,
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      1200: {
        slidesPerView: 3,
        slidesPerGroup: 3
      }
    }
  },
  pdpCarousel: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 4,
    slidesPerGroup: 4,
    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      768: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      1200: {
        slidesPerView: 4,
        slidesPerGroup: 4
      }
    }
  },
  categoryLinks: {
    slidesPerView: 11,
    spaceBetween: 0,
    freeMode: {
      enabled: true,
      sticky: false,
      momentumBounce: false
    },
    breakpoints: {
      0: {
        slidesPerView: 3
      },
      768: {
        slidesPerView: 4
      },
      992: {
        slidesPerView: 5
      },
      1200: {
        slidesPerView: 7
      },
      1600: {
        slidesPerView: 10
      }
    }
  },
  blog: {
    ...defaultConfig,
    pagination: {
      type: 'bullets'
    },
    spaceBetween: 16,
    centerInsufficientSlides: true,
    slidesPerView: 4,
    slidesPerGroup: 1,
    breakpoints: {
      0: {
        slidesPerView: 2,
        spaceBetween: 10,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 4,
        spaceBetween: 30,
        slidesPerGroup: 1
      }
    }
  },
} as ISwiperConfigs;

export default configurations;
